<template>
  <div class="change-password-page">
    <div class="container-card">
      <h1 class="title">Wijzig hier uw wachtwoord</h1>

      <p class="subtitle">
        Uw nieuwe wachtwoord dient minimaal 8 karakters, 1 kleine letter, 1 hoofdletter,
        een speciaal karakter en 1 cijfer te bevatten.
      </p>

      <div class="content-grid">
        <!-- Left column: form -->
        <div class="left">
          <label class="field-label">
            Huidig wachtwoord *
            <div class="pw-input">
              <input
                :type="show.current ? 'text' : 'password'"
                v-model="form.currentPassword"
                placeholder="Huidig wachtwoord"
                autocomplete="current-password"
              />
              <button
                type="button"
                class="icon-btn"
                @click="toggle('current')"
                :aria-pressed="show.current.toString()"
                :title="show.current ? 'Verberg wachtwoord' : 'Toon wachtwoord'"
              >
                <svg v-if="!show.current" width="20" height="20" viewBox="0 0 24 24">
                  <path fill="currentColor"
                        d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 11.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z"/>
                  <circle cx="12" cy="12" r="2.5" fill="#fff"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24">
                  <path fill="currentColor"
                        d="M2 2l20 20-1.5 1.5L.5 3.5 2 2zm6.4 6.4A4.5 4.5 0 0 0 12 17.5c.9 0 1.8-.3 2.5-.9l1.5 1.5A6.8 6.8 0 0 1 12 19.5C5 19.5 1 12.5 1 12.5c1.2-2.2 3-4 5.4-6.1l2 .9z"/>
                </svg>
              </button>
            </div>
          </label>

          <label class="field-label">
            Nieuw wachtwoord *
            <div class="pw-input">
              <input
                :type="show.new ? 'text' : 'password'"
                v-model="form.newPassword"
                placeholder="Nieuw wachtwoord"
                autocomplete="new-password"
                @input="validateAll"
              />
              <button
                type="button"
                class="icon-btn"
                @click="toggle('new')"
                :aria-pressed="show.new.toString()"
                :title="show.new ? 'Verberg wachtwoord' : 'Toon wachtwoord'"
              >
                <svg v-if="!show.new" width="20" height="20" viewBox="0 0 24 24">
                  <path fill="currentColor"
                        d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 11.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z"/>
                  <circle cx="12" cy="12" r="2.5" fill="#fff"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24">
                  <path fill="currentColor"
                        d="M2 2l20 20-1.5 1.5L.5 3.5 2 2zm6.4 6.4A4.5 4.5 0 0 0 12 17.5c.9 0 1.8-.3 2.5-.9l1.5 1.5A6.8 6.8 0 0 1 12 19.5C5 19.5 1 12.5 1 12.5c1.2-2.2 3-4 5.4-6.1l2 .9z"/>
                </svg>
              </button>
            </div>

            <div v-if="form.newPassword && !validations.lengthOk" class="input-error">
              Nieuw wachtwoord waarde is ongeldig.
            </div>
          </label>

          <label class="field-label">
            Herhaal nieuw wachtwoord *
            <div class="pw-input">
              <input
                :type="show.confirm ? 'text' : 'password'"
                v-model="form.confirmPassword"
                placeholder="Herhaal nieuw wachtwoord"
                autocomplete="new-password"
                @input="validateAll"
              />
              <button
                type="button"
                class="icon-btn"
                @click="toggle('confirm')"
                :aria-pressed="show.confirm.toString()"
                :title="show.confirm ? 'Verberg wachtwoord' : 'Toon wachtwoord'"
              >
                <svg v-if="!show.confirm" width="20" height="20" viewBox="0 0 24 24">
                  <path fill="currentColor"
                        d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 11.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z"/>
                  <circle cx="12" cy="12" r="2.5" fill="#fff"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24">
                  <path fill="currentColor"
                        d="M2 2l20 20-1.5 1.5L.5 3.5 2 2zm6.4 6.4A4.5 4.5 0 0 0 12 17.5c.9 0 1.8-.3 2.5-.9l1.5 1.5A6.8 6.8 0 0 1 12 19.5C5 19.5 1 12.5 1 12.5c1.2-2.2 3-4 5.4-6.1l2 .9z"/>
                </svg>
              </button>
            </div>

            <div v-if="form.confirmPassword && !passwordsMatch" class="input-error">
              Wachtwoorden komen niet overeen.
            </div>
          </label>

          <div class="actions">
            <button class="btn-submit" :disabled="!canSubmit" @click="submit">Wachtwoord wijzigen</button>
          </div>
        </div>

        <!-- Right column: validation checklist -->
        <div class="right" aria-hidden="false">
            <ul class="checklist">
              <li :class="{ok: validations.lengthOk}">
                <Icon :ok="validations.lengthOk" /> Langer dan 7 tekens
              </li>

              <li :class="{ok: validations.hasUpper}">
                <Icon :ok="validations.hasUpper" /> Bevat een hoofdletter
              </li>

              <li :class="{ok: validations.hasLower}">
                <Icon :ok="validations.hasLower" /> Bevat een kleine letter
              </li>

              <li :class="{ok: validations.hasDigit}">
                <Icon :ok="validations.hasDigit" /> Bevat een cijfer
              </li>

              <li :class="{ok: validations.hasSpecial}">
                <Icon :ok="validations.hasSpecial" /> Bevat een speciaal teken
              </li>
            </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapMutations } from 'vuex';
import { h } from 'vue';

const Icon = {
  name: 'Icon',
  props: { ok: { type: Boolean, default: false } },
  render() {
    if (this.ok) {
      return h('svg',
        {
          xmlns: 'http://www.w3.org/2000/svg',
          width: '16',
          height: '16',
          viewBox: '0 0 24 24',
          'aria-hidden': 'true'
        },
        [
          h('path', {
            fill: '#1aa34a',
            d: 'M9 16.2l-3.5-3.5-1.4 1.4L9 19 20 8l-1.4-1.4z'
          })
        ]
      );
    } else {
      return h('svg',
        {
          xmlns: 'http://www.w3.org/2000/svg',
          width: '16',
          height: '16',
          viewBox: '0 0 24 24',
          'aria-hidden': 'true'
        },
        [
          h('path', {
            fill: '#d33',
            d: 'M12 10.6l4.9-4.9 1.4 1.4L13.4 12l4.9 4.9-1.4 1.4L12 13.4l-4.9 4.9-1.4-1.4L10.6 12 5.7 7.1 7.1 5.7 12 10.6z'
          })
        ]
      );
    }
  }
};

export default {
  name: 'Reset_Password',
  components: { Icon },
  data() {
    return {
      // form
      form: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      // visibility toggles for each input
      show: {
        current: false,
        new: false,
        confirm: false
      },
      // validation flags
      validations: {
        lengthOk: false,
        hasUpper: false,
        hasLower: false,
        hasDigit: false,
        hasSpecial: false
      },
      emailAddress: '',
      attemptedResetPassword: false,
      posts: []
    };
  },
  mounted() {
    // initialize validation state
    this.validateAll();
  },
  computed: {
    passwordsMatch() {
      // true if new and confirm are identical and not empty
      return (
        this.form.newPassword &&
        this.form.confirmPassword &&
        this.form.newPassword === this.form.confirmPassword
      );
    },
    canSubmit() {
      // button enabled only when all validations pass, passwords match and current password present
      const v = this.validations;
      return (
        v.lengthOk &&
        v.hasUpper &&
        v.hasLower &&
        v.hasDigit &&
        v.hasSpecial &&
        this.passwordsMatch &&
        this.form.currentPassword.trim().length > 0
      );
    }
  },
  methods: {
    ...mapMutations(['toggleEveryDisplay', 'toggleHideConfig']),

    toggle(which) {
      if (which in this.show) {
        this.show[which] = !this.show[which];
      }
    },

    validateAll() {
      const pwd = this.form.newPassword || '';
      this.validations.lengthOk = pwd.length >= 8;
      this.validations.hasUpper = /[A-ZÀ-ÖØ-Þ]/.test(pwd);
      this.validations.hasLower = /[a-zà-öø-ÿ]/.test(pwd);
      this.validations.hasDigit = /[0-9]/.test(pwd);
      this.validations.hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pwd);
    },

    async submit() {
      if (!this.canSubmit) return;

      try {
        // Example: change to your actual API endpoint / request format
        // await axios.post('/api/account/changepassword', {
        //   currentPassword: this.form.currentPassword,
        //   newPassword: this.form.newPassword
        // });

        alert('Wachtwoord gewijzigd (demo). Vervang submit() met echte API-call.');

        // clear form
        this.form.currentPassword = '';
        this.form.newPassword = '';
        this.form.confirmPassword = '';
        this.validateAll();
      } catch (err) {
        console.error(err);
        alert('Er is iets misgegaan bij wijzigen van wachtwoord.');
      }
    },

    async resetPassword() {
      try {
        this.attemptedResetPassword = true;
        if (!this.emailAddress) return;

        this.$store.commit('setShowLoading', true);

        const response = await axios.post('Auth/forgotpassword', {
          email: this.emailAddress
        });

        this.posts = response.data;

        if (this.posts.success) {
          this.$showSuccess('errors.Success_check_your_mailbox');
          this.$router.push('/login');
        } else {
          this.$showError('errors.Invalid_email');
        }
      } catch (e) {
        const errorMessage = e.response?.data?.message || e.message || this.$t('errors.Something_went_wrong');
        this.$showError(`${this.$t('errors.Contact_system_admin')}: ${errorMessage}`);
      } finally {
        this.$store.commit('setShowLoading', false);
      }
    }
  }
};
</script>

<style>
.change-password-page {
  display: flex;
  justify-content: center;
  padding: 28px 12px;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
  color: #154058;
}

.container-card {
  width: 100%;
  max-width: 980px;
  background: white;
  padding: 28px 36px;
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(15,40,64,0.06);
}

/* title */
.title {
  color: #0f6b78;
  text-align: center;
  font-size: 28px;
  margin: 0 0 8px;
  font-weight: 700;
}

.subtitle {
  max-width: 720px;
  margin: 0 auto 22px;
  color: #2b6a74;
  line-height: 1.45;
  text-align: left;
  font-size: 14px;
}

/* main grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 18px;
  align-items: start;
}

/* left form */
.left {
  padding-right: 6px;
}

/* labels & inputs */
.field-label {
  display: block;
  margin-bottom: 18px;
  font-size: 14px;
  color: #134057;
}

.pw-input {
  display: flex;
  align-items: center;
  border: 1px solid #d6e2e6;
  border-radius: 6px;
  padding: 6px 10px;
  background: #fff;
  margin-top: 8px;
}

.pw-input input {
  border: none;
  outline: none;
  flex: 1;
  font-size: 14px;
  padding: 8px 6px;
  color: #133344;
  background: transparent;
}

/* icon button */
.icon-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* error under input */
.input-error {
  margin-top: 6px;
  color: #d33;
  font-size: 13px;
}

/* actions */
.actions {
  margin-top: 12px;
  display: flex;
  align-items: center;
}

.btn-submit {
  background: #c9d1d4;
  color: #ffffff;
  border: none;
  padding: 12px 22px;
  border-radius: 6px;
  font-weight: 700;
  cursor: not-allowed;
}

.btn-submit:enabled {
  background: #0f7b89;
  cursor: pointer;
}

/* right checklist */
.right {
  padding-left: 16px;
  border-left: 1px solid rgba(15,40,64,0.04);
}

.checklist {
  list-style: none;
  padding: 6px 0;
  margin: 6px 0 0;
}

.checklist li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #2b6a74;
  padding: 8px 6px;
}

.checklist li.ok {
  color: #1aa34a;
}

/* small icon alignment */
.checklist svg {
  min-width: 16px;
  min-height: 16px;
}

/* responsive */
@media (max-width: 880px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
  .right { border-left: none; padding-left: 0; margin-top: 14px; }
  .container-card { padding: 20px; }
}
</style>